import T from 'i18n';

// Helper functions for instance-based filter keys
export const getBaseKey = (instanceKey: string) => instanceKey.split('__')[0];

export const getNextInstanceKey = (baseKey: string, filterOrder: string[]) => {
  const existingInstances = filterOrder.filter((key: string) => getBaseKey(key) === baseKey);
  const instanceNumber = existingInstances.length;
  return `${baseKey}__${instanceNumber}`;
};

// Helper function to check if filter values are actually empty
export const isFilterValuesEmpty = (filterKey: string, filterValues: any) => {
  if (!filterKey || !filterValues || Object.keys(filterValues).length === 0) {
    return true;
  }

  // Check specific filter types
  if (filterKey === 'date') {
    return !filterValues.dateFrom && !filterValues.dateTo && !filterValues.dateFilter;
  } else if (filterKey === 'dateUploaded') {
    return (
      !filterValues.dateUploadedFrom &&
      !filterValues.dateUploadedTo &&
      !filterValues.dateUploadedFilter
    );
  } else if (filterKey === 'annotations') {
    return !filterValues.withAnnotations && !filterValues.withoutAnnotations;
  } else if (filterKey === 'hyperlinks') {
    return !filterValues.withPublicHyperlinks && !filterValues.withoutPublicHyperlinks;
  } else if (filterKey === 'visibility') {
    return !filterValues.private && !filterValues.public;
  } else if (filterKey === 'flag') {
    return !filterValues.withFlag && !filterValues.noFlag;
  } else if (filterKey === 'merged') {
    return !filterValues.withMerged && !filterValues.notMerged;
  } else if (filterKey === 'pageCount') {
    return !filterValues.pageCountOperator || !filterValues.pageCountValue;
  } else {
    // For array-based filters (authors, recipients, trialBundles, docType, etc.)
    const value = filterValues[filterKey];
    return !value || (Array.isArray(value) && value.length === 0);
  }
};

// Helper to get label for a specific filter instance
export const getFilterLabel = (instanceKey: string, filter: any) => {
  const baseKey = getBaseKey(instanceKey);
  const filterInstance = filter[instanceKey];

  if (!filterInstance) return null;

  // Generate label based on filter type
  switch (baseKey) {
    case 'authors': {
      const authors = filterInstance.authors || filterInstance;
      const count = Array.isArray(authors) ? authors.length : Object.keys(authors).length;
      return count > 0 ? `${T.translate('filters.authors')}: ${count}` : null;
    }
    case 'recipients': {
      const recipients = filterInstance.recipients || filterInstance;
      const count = Array.isArray(recipients) ? recipients.length : Object.keys(recipients).length;
      return count > 0 ? `${T.translate('filters.recipients')}: ${count}` : null;
    }
    case 'trialBundles': {
      const trialBundles = filterInstance.trialBundles || filterInstance;
      const count = Array.isArray(trialBundles)
        ? trialBundles.length
        : Object.keys(trialBundles).length;
      return count > 0 ? `${T.translate('filters.trialBundles')}: ${count}` : null;
    }
    case 'teamBundles': {
      const teamBundles = filterInstance.teamBundles || filterInstance;
      const count = Array.isArray(teamBundles)
        ? teamBundles.length
        : Object.keys(teamBundles).length;
      return count > 0 ? `${T.translate('filters.teamBundles')}: ${count}` : null;
    }
    case 'privateBundles': {
      const privateBundles = filterInstance.privateBundles || filterInstance;
      const count = Array.isArray(privateBundles)
        ? privateBundles.length
        : Object.keys(privateBundles).length;
      return count > 0 ? `${T.translate('filters.privateBundles')}: ${count}` : null;
    }
    case 'date':
      return filterInstance.dateFrom || filterInstance.dateTo ? T.translate('filters.date') : null;
    case 'dateUploaded':
      return filterInstance.dateUploadedFrom || filterInstance.dateUploadedTo
        ? T.translate('filters.dateUploaded')
        : null;
    case 'classifyType': {
      const classifyType = filterInstance.classifyType || filterInstance;
      const count = Array.isArray(classifyType)
        ? classifyType.length
        : Object.keys(classifyType).length;
      return count > 0 ? `${T.translate('filters.classifyType')}: ${count}` : null;
    }
    case 'classifySubtype': {
      const classifySubtype = filterInstance.classifySubtype || filterInstance;
      const count = Array.isArray(classifySubtype)
        ? classifySubtype.length
        : Object.keys(classifySubtype).length;
      return count > 0 ? `${T.translate('filters.classifySubtype')}: ${count}` : null;
    }
    case 'annotations':
      return filterInstance.withAnnotations || filterInstance.withoutAnnotations
        ? `${T.translate('filters.annotations')}: ${filterInstance.withAnnotations ? T.translate('filters.withAnnotations') : T.translate('filters.withoutAnnotations')}`
        : null;
    case 'hyperlinks':
      return filterInstance.withPublicHyperlinks || filterInstance.withoutPublicHyperlinks
        ? `${T.translate('filters.hyperlinks')}: ${filterInstance.withPublicHyperlinks ? T.translate('filters.withPublicHyperlinks') : T.translate('filters.withoutPublicHyperlinks')}`
        : null;
    case 'visibility':
      return filterInstance.private || filterInstance.public
        ? `${T.translate('filters.visibility')}: ${filterInstance.private ? T.translate('filters.privateOnly') : T.translate('filters.publicOnly')}`
        : null;
    case 'flag':
      return filterInstance.withFlag || filterInstance.noFlag
        ? `${T.translate('filters.flag')}: ${filterInstance.withFlag ? T.translate('filters.withFlag') : T.translate('filters.noFlag')}`
        : null;
    case 'merged':
      return filterInstance.withMerged || filterInstance.notMerged
        ? `${T.translate('filters.merged')}: ${filterInstance.withMerged ? T.translate('filters.withMerged') : T.translate('filters.notMerged')}`
        : null;
    case 'docType': {
      const docType = filterInstance.docType || filterInstance;
      const count = Array.isArray(docType) ? docType.length : Object.keys(docType).length;
      return count > 0 ? `${T.translate('filters.docType')}: ${count}` : null;
    }
    case 'pageCount':
      return filterInstance.pageCountOperator && filterInstance.pageCountValue
        ? `${T.translate('filters.pageCount')}: ${filterInstance.pageCountOperator} ${filterInstance.pageCountValue}`
        : null;
    default:
      return null;
  }
};

export const getAppliedFilters = (
  filterOrder: string[],
  filter: any,
  filterOperators: Record<string, 'and' | 'or'>,
) => {
  // Return filters in the order they were applied, with operator prefix
  return filterOrder
    .map((instanceKey: string, index: number) => {
      const label = getFilterLabel(instanceKey, filter);
      if (!label) return null;

      // Add operator prefix for filters after the first one
      let finalLabel = label;
      if (index > 0 && filterOperators[instanceKey]) {
        const operatorText =
          filterOperators[instanceKey] === 'and'
            ? T.translate('filters.and')
            : T.translate('filters.or');
        finalLabel = `${operatorText.toUpperCase()} ${label}`;
      }

      return {
        key: instanceKey,
        label: finalLabel,
      };
    })
    .filter(
      (item: { key: string; label: string } | null): item is { key: string; label: string } =>
        item !== null,
    );
};
